import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';

import { toastSuccess, toastError } from "../../../shared/toastHelper";

const axios = require('axios').default;

const EventImages = ({
  csrfToken,
  team,
  tour,
  tourChanged
}) => {
  const [uploadingEventImage, setUploadingEventImage] = useState(null);
  const [uploadingTourImages, setUploadingTourImages] = useState([]);
  const [completedUploads, setCompletedUploads] = useState([]);

  useEffect(() => {
    if (completedUploads.length > 0) {
      const updatedTour = {
        ...tour,
        tour_images: [...(tour.tour_images || []), ...completedUploads]
      };
      tourChanged(updatedTour);
      setCompletedUploads([]);

      toastSuccess(`${completedUploads.length} image${completedUploads.length > 1 ? 's' : ''} uploaded successfully`);
    }
  }, [completedUploads, tour, tourChanged]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadEventImage = (file) => {
    const uploadInfo = {
      id: Date.now(),
      name: file.name,
      size: formatFileSize(file.size),
      progress: 0,
      completed: false
    };
    
    setUploadingEventImage(uploadInfo);
    
    const formData = new FormData();
    formData.append('tour[event_image]', file);
    
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.put(`/teams/${team.id}/tours/${tour.id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadingEventImage(prev => ({
          ...prev,
          progress: progress
        }));
      }
    })
    .then(({ data }) => {
      setUploadingEventImage(prev => ({
        ...prev,
        progress: 100,
        completed: true
      }));
      
      setTimeout(() => {
        tourChanged(data);
        setUploadingEventImage(null);
        toastSuccess("Event image uploaded successfully");
      }, 500);
    })
    .catch((error) => {
      setUploadingEventImage(null);
      toastError(error.response?.data?.join(", ") || "Failed to upload event image");
    });
  };

  const uploadTourImage = (file) => {
    const tempId = Date.now() + Math.random();
    const uploadInfo = {
      id: tempId,
      name: file.name,
      size: formatFileSize(file.size),
      progress: 0,
      completed: false
    };
    
    setUploadingTourImages(prev => [...prev, uploadInfo]);
    
    const formData = new FormData();
    formData.append('tour_image[image]', file);
    
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.post(`/teams/${team.id}/tours/${tour.id}/tour_images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadingTourImages(prev => 
          prev.map(img => 
            img.id === tempId 
              ? { ...img, progress: progress }
              : img
          )
        );
      }
    })
    .then(({ data }) => {
      setUploadingTourImages(prev => 
        prev.map(img => 
          img.id === tempId 
            ? { ...img, progress: 100, completed: true }
            : img
        )
      );
      
      setTimeout(() => {
        setUploadingTourImages(prev => prev.filter(img => img.id !== tempId));
        setCompletedUploads(prev => [...prev, data]);
      }, 500);
    })
    .catch((error) => {
      setUploadingTourImages(prev => prev.filter(img => img.id !== tempId));
      toastError(error.response?.data?.join(", ") || "Failed to upload additional image");
    });
  };

  const deleteTourImage = (tourImageId) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.delete(`/teams/${team.id}/tours/${tour.id}/tour_images/${tourImageId}`)
      .then(() => {
        const updatedTour = {
          ...tour,
          tour_images: (tour.tour_images || []).filter(img => img.id !== tourImageId)
        };
        tourChanged(updatedTour);
        
        toastSuccess("Image deleted successfully");
      })
      .catch((error) => {
        toastError("Failed to delete image");
      });
  };

  const deleteEventImage = () => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.put(`/teams/${team.id}/tours/${tour.id}`, {
      tour: {
        event_image: null
      }
    })
    .then(({ data }) => {
      tourChanged(data);
      toastSuccess("Event image deleted successfully");
    })
    .catch((error) => {
      toastError("Failed to delete event image");
    });
  };

  const UploadProgressCard = ({ upload }) => {
    const getFileExtension = (filename) => {
      const lastDot = filename.lastIndexOf('.');
      if (lastDot === -1) return 'FILE';
      const extension = filename.substring(lastDot + 1).toUpperCase();
      return extension.length > 4 ? extension.substring(0, 4) : extension;
    };

    return (
      <div style={{
        border: '1px solid #E9EAEB',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '12px',
        backgroundColor: 'white'
      }}>
        <div className="d-flex align-items-start">
          <div style={{
            width: '32px',
            height: '32px',
            backgroundColor: '#2563EB',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            flexShrink: 0
          }}>
            <span style={{
              color: 'white',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              {getFileExtension(upload.name)}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {upload.name}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '2px'
            }}>
              {upload.size}
            </div>
            <div className="d-flex align-items-center">
              <div style={{
                flex: 1,
                height: '4px',
                backgroundColor: '#E5E7EB',
                borderRadius: '2px',
                overflow: 'hidden',
                marginRight: '12px'
              }}>
                <div style={{
                  width: `${upload.progress}%`,
                  height: '100%',
                  backgroundColor: '#2563EB',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                flexShrink: 0
              }}>
                {upload.progress}%
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{
        marginTop: '16px',
        marginBottom: '20px',
        paddingBottom: '24px'
      }}>
        <div className='row'>
          <div className='col-12 d-flex justify-content-between'>
            <div>
              <p className="mb-0" style={{fontSize: '18px', fontWeight: '600'}}>
                Event Images
              </p>
              <p className="mb-0" style={{fontSize: '14px'}}>
                Add images to your event to give fans a preview of what to expect.
              </p>
            </div>
            <div>
              <span className="badge badge-light small"
                    style={{
                      backgroundColor: "#FAFAFA",
                      border: "1px solid #E9EAEB",
                      color: "#414651",
                      padding: "2px 8px",
                      fontWeight: "400"
                    }}>
                Optional
              </span>
            </div>
          </div>
        </div>
      </div>
      <div style={{marginBottom: '25px'}}>
        <div className="form-row" style={{marginBottom: '20px'}}>
          <div className="col-12 col-xl-4 mb-3 mb-xl-0">
            <p className='mb-0'
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                }}>
              Event Image
            </p>
            <p className='mb-0 text-muted'
                style={{
                  fontSize: '14px',
                }}>
              Upload your main event image here. This image will appear on the ticketing widget.
            </p>
          </div>
          <div className="col-12 col-xl-8">
            <div>
              {tour.compressed_event_image_url ? (
                <div style={{
                  position: 'relative',
                  border: '1px solid #E9EAEB',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  aspectRatio: '2 / 1',
                  width: '100%'
                }}>
                  <img 
                    src={tour.compressed_event_image_url} 
                    alt="Event" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    display: 'flex',
                    gap: '12px'
                  }}>
                    <Dropzone 
                      maxFiles={1}
                      accept="image/*"
                      onDrop={(files) => {
                        if (files.length > 0) {
                          uploadEventImage(files[0]);
                        }
                      }}
                    >
                      {({getRootProps, getInputProps}) => (
                        <div {...getRootProps()}>
                          <input {...getInputProps()} />
                          <button 
                            type="button"
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #D5D7DA',
                              borderRadius: '6px',
                              fontSize: '16px',
                              padding: '6px',
                              cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              width: "40px",
                              height: "40px"
                            }}
                            disabled={uploadingEventImage}
                            title="Replace image"
                          >
                            <i className="fas fa-retweet-alt" style={{color: 'black'}}></i>
                          </button>
                        </div>
                      )}
                    </Dropzone>
                    <button 
                      type="button"
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #D5D7DA',
                        borderRadius: '6px',
                        fontSize: '16px',
                        padding: '6px',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        width: "40px",
                        height: "40px"
                      }}
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this image?')) {
                          deleteEventImage();
                        }
                      }}
                      title="Delete image"
                    >
                      <i className="far fa-trash-alt" style={{color: 'black'}}></i>
                    </button>
                  </div>
                </div>
              ) : (
                <Dropzone 
                  maxFiles={1}
                  accept="image/*"
                  onDrop={(files) => {
                    if (files.length > 0) {
                      uploadEventImage(files[0]);
                    }
                  }}
                >
                  {({getRootProps, getInputProps}) => (
                    <section 
                      {...getRootProps()}
                      style={{
                        border: '1px solid #E9EAEB',
                        borderRadius: '12px',
                        padding: '16px 24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: uploadingEventImage ? '#f8f9fa' : 'transparent'
                      }}
                    >
                      <input {...getInputProps()} />
                      {uploadingEventImage ? (
                        <div>
                          <img src="/uploading-loading.gif" style={{width: "25px", marginBottom: '10px'}} />
                          <p className="mb-0">Uploading...</p>
                        </div>
                      ) : (
                        <div>
                          <div className='d-flex align-items-center justify-content-center mb-3 shadow-1'
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #E9EAEB',
                                  width: "40px",
                                  height: "40px",
                                  margin: "0 auto",
                                }}>
                            <i className="far fa-cloud-upload-alt text-dark" style={{
                              fontSize: "16px"
                            }}></i>
                          </div>
                          <p className="mb-0"
                              style={{
                                fontSize: '14px',
                                color: "#535862"
                              }}>
                            <strong style={{color: '#136395'}}>Click to upload</strong> or drag and drop<br/>
                            <span style={{fontSize: '12px'}}>SVG, PNG, JPG or GIF</span>
                          </p>
                        </div>
                      )}
                    </section>
                  )}
                </Dropzone>
              )}

              {uploadingEventImage && (
                <div style={{marginTop: '12px'}}>
                  <UploadProgressCard upload={uploadingEventImage} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="form-row" style={{margin: '0px 0px 25px 0px'}}>
        <div className="col-12">
          <hr style={{borderTop: '1px solid #E9EAEB', marginTop: '0px', marginBottom: '0px'}} />
        </div>
      </div>
      <div>
        <div className="form-row" style={{marginBottom: '20px'}}>
          <div className="col-12 col-xl-4 mb-3 mb-xl-0">
            <p className='mb-0'
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                }}>
              Additional Images
            </p>
            <p className='mb-0 text-muted'
                style={{
                  fontSize: '14px'
                }}>
              Upload additional tour images here.
            </p>
          </div>
          <div className="col-12 col-xl-8">
            <Dropzone 
              accept="image/*,.psd,application/octet-stream"
              onDrop={(files) => {
                files.forEach(file => uploadTourImage(file));
              }}
            >
              {({getRootProps, getInputProps}) => (
                <section 
                  {...getRootProps()}
                  style={{
                    border: '1px solid #E9EAEB',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    marginBottom: '20px'
                  }}
                >
                  <input {...getInputProps()} />
                  <div>
                    <div className='d-flex align-items-center justify-content-center mb-3 shadow-1'
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #E9EAEB',
                            width: "40px",
                            height: "40px",
                            margin: "0 auto",
                          }}>
                      <i className="far fa-cloud-upload-alt text-dark" style={{
                        fontSize: "16px"
                      }}></i>
                    </div>
                    <p className="mb-0"
                        style={{
                          fontSize: '14px',
                          color: "#535862"
                        }}>
                      <strong style={{color: '#136395'}}>Click to upload</strong> or drag and drop<br/>
                      <span style={{fontSize: '12px'}}>SVG, PNG, JPG, GIF or PSD</span>
                    </p>
                  </div>
                </section>
              )}
            </Dropzone>

            {uploadingTourImages.length > 0 && (
              <div style={{marginBottom: '20px'}}>
                {uploadingTourImages.map(upload => (
                  <UploadProgressCard key={upload.id} upload={upload} />
                ))}
              </div>
            )}

            {tour.tour_images && tour.tour_images.length > 0 && (
              <div className="row">
                {tour.tour_images.map((tourImage) => (
                  <div key={tourImage.id} className="col-6 col-sm-4 col-md-6 col-xl-4 mb-3">
                    <div style={{
                      position: 'relative',
                      border: '1px solid #E9EAEB',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      aspectRatio: '1',
                      width: '100%'
                    }}>
                      <img 
                        src={tourImage.thumb_image_url} 
                        alt="Tour" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px'
                      }}>
                        <button 
                          type="button"
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #D5D7DA',
                            borderRadius: '6px',
                            fontSize: '14px',
                            padding: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            width: "28px",
                            height: "28px"
                          }}
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this image?')) {
                              deleteTourImage(tourImage.id);
                            }
                          }}
                        >
                          <i className="far fa-trash-alt" style={{color: 'black'}}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EventImages.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  tour: PropTypes.object.isRequired,
  tourChanged: PropTypes.func.isRequired
};

export default EventImages;
